/**
 * How a data room measures presence — 20.1.0 §8.
 *
 * Total Viewing Time is a room-level presence clock, not a per-file measurement, and it counts
 * only time with the room in focus. So the server cannot read it off the wall clock: the span from
 * the first beat to the last would include every minute the visitor spent in their email. It is
 * counted instead, one whole interval at a time, from beats that arrived while the tab was in
 * focus — a pause simply produces no beats and therefore no time.
 *
 * The interval is the unit of the measurement, which is why it lives here rather than on either
 * side: the client beats at it and the analytics multiply by it.
 */
export const DATA_ROOM_HEARTBEAT_INTERVAL_MS = 30_000;

/**
 * The earliest a beat may be credited, and the latest.
 *
 * Below the floor nothing is credited and nothing is written — that is what stops a client from
 * drawing itself time by beating faster, since the floor is measured on the server's clock and a
 * beat can only ever vouch for the one interval behind it. The ten per cent of slack absorbs the
 * jitter of a browser timer, so an honest client is never charged for its own lateness.
 *
 * Above the ceiling the gap is read as a pause: the visitor is back, but the time they were away
 * is not theirs. Two intervals, so a single dropped beat is forgiven and a real pause is not.
 */
export const DATA_ROOM_HEARTBEAT_MIN_GAP_MS = DATA_ROOM_HEARTBEAT_INTERVAL_MS * 0.9;

export const DATA_ROOM_HEARTBEAT_MAX_GAP_MS = DATA_ROOM_HEARTBEAT_INTERVAL_MS * 2;

/** 20.1.0 §8: focus lost or idle beyond this closes the session permanently. */
export const DATA_ROOM_SESSION_IDLE_MS = 30 * 60_000;

export enum DataRoomHeartbeatEffect {
  /** The visitor was here for the interval just past. */
  CREDIT = 'credit',
  /** Too soon to vouch for anything; the row is left exactly as it was. */
  IGNORE = 'ignore',
  /** They were away and are back — the session continues, the gap is not counted. */
  PAUSE = 'pause',
  /** Nothing open to continue: a session of their own. */
  START = 'start',
}

/**
 * Whether a session is still running, which is not a column: §8 leaves the ordinary end to be read
 * off how long ago the visitor was last seen. A View or a Download lands in the session this is
 * true of, and in no session at all when it is not.
 */
export const isDataRoomSessionOpen = ({
  lastSeenAt,
  now,
}: {
  lastSeenAt: Date;
  now: Date;
}): boolean => now.getTime() - lastSeenAt.getTime() <= DATA_ROOM_SESSION_IDLE_MS;

export const dataRoomHeartbeatEffect = ({
  lastSeenAt,
  now,
}: {
  lastSeenAt: Date | null;
  now: Date;
}): DataRoomHeartbeatEffect => {
  if (!lastSeenAt) return DataRoomHeartbeatEffect.START;

  if (!isDataRoomSessionOpen({ lastSeenAt, now })) return DataRoomHeartbeatEffect.START;

  const gap = now.getTime() - lastSeenAt.getTime();

  if (gap < DATA_ROOM_HEARTBEAT_MIN_GAP_MS) return DataRoomHeartbeatEffect.IGNORE;
  if (gap > DATA_ROOM_HEARTBEAT_MAX_GAP_MS) return DataRoomHeartbeatEffect.PAUSE;

  return DataRoomHeartbeatEffect.CREDIT;
};

/**
 * What one session is worth. Counted intervals, never a span: `lastSeenAt - startedAt` would hand
 * back the pauses the visitor spent elsewhere.
 */
export const dataRoomViewingTimeMs = (heartbeats: number): number =>
  heartbeats * DATA_ROOM_HEARTBEAT_INTERVAL_MS;
